import { Phone, Users, ClipboardList, HardHat, ShieldCheck, ClipboardCheck } from "lucide-react";
import Reveal from "../components/Reveal";
import { PageHero } from "../components/Shared";
import SEO from "../components/SEO";

const CORE_TEAM = [
  { name: "Srivatsa B.P", role: "Managing Director", phone: null, tel: null },
  { name: "Manish Udupa", role: "Admin & Commercial", phone: "+91 91080 27356", tel: "+919108027356" },
  { name: "Sanjay", role: "Lead Electrical Engineer", phone: null, tel: null },
];

const EXECUTION = [
  { icon: Users, title: "Leadership Involvement", desc: "Dedicated leadership oversight at all key project meetings." },
  { icon: ClipboardList, title: "Single Point Contact", desc: "A dedicated Project Manager leads all on-site decisions." },
  { icon: HardHat, title: "Expert Execution", desc: "Skilled engineers handle every specialised task on site." },
  { icon: ShieldCheck, title: "Quality Control", desc: "Rigorous testing carried out to spec and safety standards." },
  { icon: ClipboardCheck, title: "Commissioning", desc: "Joint testing with the client and final system handover." },
];

const CONSULTANTS = ["AN Prakash PMC", "DNA Consulting", "Jones Lang LaSalle", "Atelier", "ABM Associates", "AEC Infotech", "IMG Consultants", "CBRE", "C&W"];
const ELECTRICAL = ["ACE Consultants", "Akash Consultants", "MECA Project Engineers", "Om Poorna", "SAN Electrical", "Power Line", "Potential Services", "Sripeksha", "Power Design India", "Dhruva Engineering", "MJ Consultants"];
const SAFETY = ["JEF Techno"];

export default function About() {
  return (
    <>
      <SEO
        title="About Us | SUV Engineers"
        description="Learn about SUV Engineers, our expert leadership, team structure, standard operating procedures, and our history of electrical project execution."
      />
      <PageHero crumb="About" title="Electrical contracting expertise shaped by site realities.">
        Over three decades of specialised engineering, delivered by a close-knit core team and skilled
        site engineers across industrial, commercial and infrastructure projects.
      </PageHero>

      {/* OUR STORY */}
      <section className="section">
        <div className="section-inner split-grid">
          <Reveal>
            <p className="eyebrow">Our Story</p>
            <h2 style={{ fontSize: "clamp(1.9rem,3.6vw,2.9rem)" }}>Decades of expertise, under one roof.</h2>
          </Reveal>
          <Reveal className="content-stack" delay={120}>
            <p>
              <strong>SUV Engineers</strong> brings together decades of expertise in engineering and
              contracting, with over <strong>30 years</strong> of specialised experience in the field.
            </p>
            <p>
              The company has successfully executed numerous projects across various sectors including IT,
              Industrial, Commercial, Base Buildings and Layouts — alongside comprehensive electrical works
              spanning internal, interior, external, LT and HT installations.
            </p>
            <p>
              As <strong>Government Licensed Class-I Contractors</strong>, we pride ourselves on delivering
              high-quality work that meets and exceeds industry standards. Our expertise spans from traditional
              electrical installations to modern networking and security systems — making us a comprehensive
              solution provider for all your engineering needs.
            </p>
          </Reveal>
        </div>
      </section>

      {/* PROJECT EXECUTION (above Core Team) */}
      <section className="section dark">
        <div className="section-inner">
          <Reveal className="section-head center">
            <p className="eyebrow" style={{ justifyContent: "center" }}>Project Execution</p>
            <h2>What you can expect from SUV Engineers.</h2>
            <p>A disciplined, transparent process — from the first meeting to final handover — that keeps
              every project on spec, on schedule and safe.</p>
          </Reveal>
          <div className="pe-flow" data-testid="execution-timeline">
            {EXECUTION.map((s, i) => (
              <Reveal key={s.title} className="pe-step" delay={i * 90}>
                <span className="pe-node">
                  <s.icon />
                  <span className="num">{i + 1}</span>
                </span>
                <div className="pe-card">
                  <h3>{s.title}</h3>
                  <p>{s.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* CORE TEAM */}
      <section className="section muted">
        <div className="section-inner">
          <Reveal className="section-head center">
            <p className="eyebrow" style={{ justifyContent: "center" }}>Core Team</p>
            <h2>The people behind the work.</h2>
          </Reveal>
          <div className="partners-grid">
            {CORE_TEAM.map((p, i) => (
              <Reveal key={p.name} className="partner-card" delay={i * 90}>
                <div className="partner-avatar">{p.name.split(" ").map((w) => w[0]).slice(0, 2).join("")}</div>
                <h3>{p.name}</h3>
                <div className="role">{p.role}</div>
                {p.tel && <a href={`tel:${p.tel}`}><Phone /> {p.phone}</a>}
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* NETWORK */}
      <section className="section">
        <div className="section-inner">
          <Reveal className="section-head">
            <p className="eyebrow">Our Network</p>
            <h2>Consultants &amp; collaborators we work with.</h2>
          </Reveal>
          <Reveal>
            <h3 style={{ fontSize: "1.1rem", marginBottom: 14 }}>Project Management Consultants</h3>
            <div className="logo-cloud" style={{ marginBottom: 32 }}>
              {CONSULTANTS.map((c) => <span key={c} className="pill">{c}</span>)}
            </div>
            <h3 style={{ fontSize: "1.1rem", marginBottom: 14 }}>Electrical Consultants</h3>
            <div className="logo-cloud" style={{ marginBottom: 32 }}>
              {ELECTRICAL.map((c) => <span key={c} className="pill">{c}</span>)}
            </div>
            <h3 style={{ fontSize: "1.1rem", marginBottom: 14 }}>Safety Consultants</h3>
            <div className="logo-cloud">
              {SAFETY.map((c) => <span key={c} className="pill">{c}</span>)}
            </div>
          </Reveal>
        </div>
      </section>
    </>
  );
}
